import React, { useEffect, useState } from "react";
import { MdClose } from "react-icons/md";
import { FiCheck, FiX } from "react-icons/fi";
import { createPortal } from "react-dom";
import { useClickOutside } from "./../../Hooks/useClickOutside";

const SampleApprovalModal = ({
  open,
  onClose,
  onConfirm,
  action, // "Approved" | "Rejected"
  loading = false,
  declarationChecked,
  setDeclarationChecked,
}) => {
  const [animate, setAnimate] = useState(false);
  const [remark, setRemark] = useState("");

  const isApprove = action === "Approved";
  const isReject = action === "Rejected";

  const canConfirm =
    !loading &&
    (isApprove ? declarationChecked : remark.trim().length > 0);

  const handleClose = () => {
    if (loading) return;
    setAnimate(false);
    setTimeout(() => {
      onClose();
      setRemark("");
    }, 250);
  };

  const domNode = useClickOutside(() => handleClose());

  useEffect(() => {
    if (open) {
      setRemark("");
      setTimeout(() => setAnimate(true), 10);
    } else {
      setAnimate(false);
    }
  }, [open]);

  if (!open) return null;

  const headerGradient = isApprove
    ? "from-green-500 via-emerald-500 to-green-600"
    : "from-red-500 via-rose-500 to-red-600";

  const accentColor = isApprove ? "green" : "red";

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-end md:items-center justify-center
        bg-black/30 backdrop-blur-sm transition-opacity duration-300
        ${animate ? "opacity-100" : "opacity-0"}`}
    >
      <div
        ref={domNode}
        className={`w-full md:w-[540px] bg-white shadow-2xl md:rounded-3xl rounded-t-3xl
          transition-all duration-300
          ${animate ? "scale-100 translate-y-0" : "scale-95 md:translate-y-4 translate-y-full"}`}
      >
        {/* HEADER */}
        <div
          className={`flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r ${headerGradient} md:rounded-t-3xl rounded-t-3xl`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              {isApprove ? (
                <FiCheck className="text-white" size={22} />
              ) : (
                <FiX className="text-white" size={22} />
              )}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">
                {isApprove ? "Approve Sample" : "Reject Sample"}
              </h2>
              <p className="text-xs text-white/70">
                {isApprove
                  ? "Confirm sample quality acceptance"
                  : "Provide a reason for rejection"}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-2 bg-white rounded-full hover:bg-white/80 transition disabled:opacity-50"
          >
            <MdClose
              size={20}
              className={isApprove ? "text-green-600" : "text-red-600"}
            />
          </button>
        </div>

        {/* BODY */}
        <div className="px-6 py-5 space-y-4">
          {/* Declaration checkbox — approval only */}
          {isApprove && (
            <label
              className={`flex items-start gap-3 p-4 rounded-2xl border cursor-pointer
                ${declarationChecked
                  ? "border-green-300 bg-green-50"
                  : "border-gray-200 bg-gray-50"
                }`}
            >
              <input
                type="checkbox"
                checked={declarationChecked}
                onChange={(e) => setDeclarationChecked(e.target.checked)}
                className="mt-1 w-4 h-4 accent-green-600 flex-shrink-0"
              />
              <span className="text-sm text-gray-700 leading-relaxed">
                I confirm that I have physically verified the received sample
                and am satisfied with the product quality, specifications,
                packaging, and condition. I understand that this approval will
                be treated as acceptance to proceed further with the order.
              </span>
            </label>
          )}

          {/* Remark textarea */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              {isApprove ? "Remarks" : "Reason for Rejection"}
              {isReject && (
                <span className="ml-1 text-red-500">*</span>
              )}
              {isApprove && (
                <span className="ml-1 text-gray-400 font-normal normal-case">
                  (optional)
                </span>
              )}
            </label>
            <textarea
              rows={3}
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder={
                isApprove
                  ? "Any additional comments about the sample quality..."
                  : "Describe why this sample does not meet your requirements..."
              }
              className={`w-full resize-none rounded-2xl border px-4 py-3 text-sm text-gray-800 outline-none transition
                placeholder:text-gray-400
                ${isReject && remark.trim().length === 0
                  ? "border-red-200 bg-red-50/40 focus:border-red-400"
                  : "border-gray-200 bg-gray-50 focus:border-gray-400"
                }`}
            />
            {isReject && remark.trim().length === 0 && (
              <p className="text-xs text-red-500 mt-1 font-medium">
                A reason is required to reject the sample.
              </p>
            )}
          </div>
        </div>

        {/* FOOTER */}
        <div className="flex gap-3 px-5 py-5 border-t">
          <button
            onClick={handleClose}
            disabled={loading}
            className="flex-1 py-3.5 border-2 border-gray-300 text-gray-700 rounded-2xl font-medium hover:bg-gray-50 transition disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            onClick={() => onConfirm(remark.trim())}
            disabled={!canConfirm}
            className={`flex-1 py-3.5 text-white font-semibold rounded-2xl transition-all duration-300
              flex items-center justify-center gap-2 disabled:opacity-60
              ${isApprove
                ? "bg-gradient-to-r from-green-500 to-emerald-600 hover:shadow-[0px_6px_16px_rgba(16,185,129,0.4)]"
                : "bg-gradient-to-r from-red-500 to-rose-600 hover:shadow-[0px_6px_16px_rgba(239,68,68,0.4)]"
              }`}
          >
            {loading && (
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            )}
            {loading
              ? "Processing..."
              : isApprove
                ? "Yes, Approve"
                : "Yes, Reject"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SampleApprovalModal;